import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  Plus, Trash, CaretLeft, Check, FloppyDisk,
  CurrencyDollar, Percent, Users, ChartLine
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = {
  get: (url) => axios.get(`${API}${url}`),
  put: (url, data) => axios.put(`${API}${url}`, data),
};

const formatCurrency = (amount, currency = "INR") => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount || 0);
};

export const RulesConfiguration = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("revenue");
  
  // Rules state
  const [fixedRule, setFixedRule] = useState({
    fixed_fee_type: "flat",
    fixed_fee_amount: 0,
    fixed_fee_percentage: 0,
    revenue_threshold: 0,
    conditional_fixed_fee_enabled: false,
    conditional_fixed_fee_amount: 0,
    conditional_logic_type: "roi",
    conditional_threshold: 0,
  });
  
  const [variableRule, setVariableRule] = useState({
    enabled: false,
    trigger_metric: "net_sales",
    threshold_value: 0,
    calculation_type: "flat",
    flat_percentage: 0,
  });
  
  const [slabRules, setSlabRules] = useState([]);
  
  const [incentiveRule, setIncentiveRule] = useState({
    incentive_type: "revenue",
    trigger_start: 0,
    increment_step: 0,
    extra_percentage_per_step: 0,
    cap_percentage: 0,
    roi_threshold: 0,
    roi_base_percentage: 0,
    roi_increment_step: 1,
    roi_increment_percentage: 0,
    roi_max_cap: 0,
    roi_max_percentage_cap: 0,
    roi_calculation_base: "total_fee",
  });
  
  const [commissionRule, setCommissionRule] = useState({
    enabled: false,
    team_member_name: "",
    fixed_payout: 0,
    variable_percentage: 0,
    based_on: "total",
  });

  const [includeReturns, setIncludeReturns] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/clients/${clientId}`);
        setClient(res.data.client);
        setIncludeReturns(res.data.client?.include_returns_in_revenue || false);
        
        const rules = res.data.rules || {};
        if (rules.fixed) setFixedRule({ ...fixedRule, ...rules.fixed });
        if (rules.variable) setVariableRule({ ...variableRule, ...rules.variable });
        if (rules.slabs) setSlabRules(rules.slabs);
        if (rules.incentive) setIncentiveRule({ ...incentiveRule, ...rules.incentive });
        if (rules.commission) setCommissionRule({ ...commissionRule, ...rules.commission });
      } catch (err) {
        console.error("Failed to fetch client:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  const saveFixedRule = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/rules/fixed`, { ...fixedRule, client_id: clientId });
      alert("Fixed fee rule saved!");
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveVariableRule = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/rules/variable`, { ...variableRule, client_id: clientId });
      alert("Variable fee rule saved!");
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveSlabRules = async () => {
    setSaving(true);
    try {
      const slabsWithIds = slabRules.map((s, i) => ({ ...s, id: s.id || `slab-${i}`, client_id: clientId }));
      await api.put(`/clients/${clientId}/rules/slabs`, slabsWithIds);
      alert("Slab rules saved!");
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveIncentiveRule = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/rules/incentive`, { ...incentiveRule, client_id: clientId });
      alert("Incentive rule saved!");
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveCommissionRule = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/rules/commission`, { ...commissionRule, client_id: clientId });
      alert("Commission rule saved!");
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveRevenueSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, { include_returns_in_revenue: includeReturns });
      alert("Revenue settings saved!");
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addSlab = () => {
    setSlabRules([...slabRules, { min_value: 0, max_value: 0, percentage: 0, apply_on_slab: true }]);
  };

  const removeSlab = (index) => {
    setSlabRules(slabRules.filter((_, i) => i !== index));
  };

  const updateSlab = (index, field, value) => {
    const updated = [...slabRules];
    updated[index] = { ...updated[index], [field]: value };
    setSlabRules(updated);
  };

  if (loading) {
    return <div className="p-8 text-[#A1A1AA]">Loading...</div>;
  }

  return (
    <div data-testid="rules-config-page">
      <div className="flex items-center gap-4 mb-8">
        <Link to={`/clients/${clientId}`} className="btn-ghost">
          <CaretLeft size={20} />
        </Link>
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Varela Round' }}>
            Configure Rules
          </h1>
          <p className="text-[#52525B] mt-1">{client?.client_name} - {client?.project_name}</p>
        </div>
      </div>

      <div className="tabs">
        {[
          { id: "revenue", label: "Revenue Settings", icon: ChartLine },
          { id: "fixed", label: "Fixed Fee", icon: CurrencyDollar },
          { id: "variable", label: "Variable Fee", icon: Percent },
          { id: "slabs", label: "Slabs", icon: ChartLine },
          { id: "incentive", label: "Incentives", icon: ChartLine },
          { id: "commission", label: "Commission", icon: Users },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`tab flex items-center gap-2 ${activeTab === tab.id ? "active" : ""}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "revenue" && (
        <div className="card-flat p-6 max-w-2xl" data-testid="revenue-settings-tab">
          <h3 className="text-lg font-semibold mb-6">Revenue Settings</h3>
          <p className="text-sm text-[#A1A1AA] mb-6">
            Configure how Shopify revenue is calculated for billing. Total Sales from Shopify is used as the base (gross_sales - discounts + returns + shipping + taxes).
          </p>
          <div className="space-y-6">
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="include-returns" checked={includeReturns}
                  onChange={(e) => setIncludeReturns(e.target.checked)}
                  className="w-5 h-5 accent-[#FFEB00] cursor-pointer" data-testid="include-returns-checkbox" />
                <label htmlFor="include-returns" className="cursor-pointer">
                  <span className="font-medium text-base">Include returns back in sales</span>
                  <p className="text-sm text-[#71717A] mt-1">
                    When enabled, returned amounts will be added back to the Total Sales figure before billing calculation.
                    Use this when returns should not reduce the billable revenue.
                  </p>
                </label>
              </div>
            </div>
            <div className="p-4 bg-[#0A0A0A] border border-[#2A2A2A] rounded text-sm text-[#A1A1AA]">
              <p className="font-medium text-white mb-2">How it works:</p>
              <p>Shopify Total Sales = Gross Sales - Discounts - Returns + Shipping + Taxes</p>
              <p className="mt-1">If checked: <span className="text-[#FFEB00]">Billing Revenue = Total Sales + |Returns|</span></p>
              <p>If unchecked: <span className="text-[#FFEB00]">Billing Revenue = Total Sales</span> (returns already deducted)</p>
            </div>
            <button onClick={saveRevenueSettings} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-revenue-settings-btn">
              <FloppyDisk size={18} /> {saving ? "Saving..." : "Save Revenue Settings"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "fixed" && (
        <div className="card-flat p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-6">
            Fixed Fee Configuration
          </h3>
          
          <div className="space-y-6">
            <div className="form-group">
              <label className="form-label">Fixed Fee Type</label>
              <select
                className="input-default w-full"
                value={fixedRule.fixed_fee_type}
                onChange={(e) => setFixedRule({ ...fixedRule, fixed_fee_type: e.target.value })}
                data-testid="select-fixed-fee-type"
              >
                <option value="flat">Flat Amount</option>
                <option value="percentage">Percentage of Revenue</option>
              </select>
              <p className="text-xs text-[#71717A] mt-2">
                {fixedRule.fixed_fee_type === "flat" 
                  ? "A fixed amount charged regardless of revenue"
                  : "Fee calculated as a percentage of revenue, up to a defined threshold"}
              </p>
            </div>

            {fixedRule.fixed_fee_type === "flat" ? (
              <div className="form-group">
                <label className="form-label">Fixed Fee Amount ({client?.currency})</label>
                <input
                  type="number"
                  className="input-default w-full"
                  value={fixedRule.fixed_fee_amount}
                  onChange={(e) => setFixedRule({ ...fixedRule, fixed_fee_amount: parseFloat(e.target.value) || 0 })}
                  data-testid="input-fixed-fee"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="form-group">
                  <label className="form-label">Fixed Fee Percentage (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-default w-full"
                    value={fixedRule.fixed_fee_percentage}
                    onChange={(e) => setFixedRule({ ...fixedRule, fixed_fee_percentage: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Revenue Threshold ({client?.currency})</label>
                  <input
                    type="number"
                    className="input-default w-full"
                    value={fixedRule.revenue_threshold}
                    onChange={(e) => setFixedRule({ ...fixedRule, revenue_threshold: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="col-span-2 p-3 bg-[#0A0A0A] border border-[#FFEB00]/20">
                  <p className="text-xs text-[#A1A1AA]">
                    <strong className="text-[#FFEB00]">How it works:</strong> Fixed fee = {fixedRule.fixed_fee_percentage || 0}% of revenue, applied only when revenue is below {client?.currency} {(fixedRule.revenue_threshold || 0).toLocaleString()}. Once revenue exceeds this threshold, this fee stops and the next billing rule applies.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-4 border-t border-[#2A2A2A]">
              <input
                type="checkbox"
                id="conditional-enabled"
                checked={fixedRule.conditional_fixed_fee_enabled}
                onChange={(e) => setFixedRule({ ...fixedRule, conditional_fixed_fee_enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="conditional-enabled" className="form-label mb-0">
                Enable Conditional Fixed Fee
              </label>
            </div>

            {fixedRule.conditional_fixed_fee_enabled && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="form-group">
                  <label className="form-label">Conditional Fee Amount</label>
                  <input
                    type="number"
                    className="input-default w-full"
                    value={fixedRule.conditional_fixed_fee_amount}
                    onChange={(e) => setFixedRule({ ...fixedRule, conditional_fixed_fee_amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Logic Type</label>
                  <select
                    className="input-default w-full"
                    value={fixedRule.conditional_logic_type}
                    onChange={(e) => setFixedRule({ ...fixedRule, conditional_logic_type: e.target.value })}
                  >
                    <option value="roi">ROI Based</option>
                    <option value="revenue">Revenue Based</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Threshold (Apply conditional fee when metric &gt;=)</label>
                  <input
                    type="number"
                    className="input-default w-full"
                    value={fixedRule.conditional_threshold}
                    onChange={(e) => setFixedRule({ ...fixedRule, conditional_threshold: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            )}

            <button onClick={saveFixedRule} className="btn-primary flex items-center gap-2" disabled={saving}>
              <FloppyDisk size={18} />
              {saving ? "Saving..." : "Save Fixed Fee Rule"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "variable" && (
        <div className="card-flat p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Varela Round' }}>
            Variable Fee Configuration
          </h3>
          
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="variable-enabled"
                checked={variableRule.enabled}
                onChange={(e) => setVariableRule({ ...variableRule, enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="variable-enabled" className="form-label mb-0">
                Enable Variable Fee
              </label>
            </div>

            {variableRule.enabled && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Trigger Metric</label>
                    <select
                      className="input-default w-full"
                      value={variableRule.trigger_metric}
                      onChange={(e) => setVariableRule({ ...variableRule, trigger_metric: e.target.value })}
                    >
                      <option value="net_sales">Net Sales</option>
                      <option value="gross_sales">Gross Sales</option>
                      <option value="roi">ROI</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Threshold Value</label>
                    <input
                      type="number"
                      className="input-default w-full"
                      value={variableRule.threshold_value}
                      onChange={(e) => setVariableRule({ ...variableRule, threshold_value: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Calculation Type</label>
                    <select
                      className="input-default w-full"
                      value={variableRule.calculation_type}
                      onChange={(e) => setVariableRule({ ...variableRule, calculation_type: e.target.value })}
                    >
                      <option value="flat">Flat Percentage</option>
                      <option value="slab">Slab Based</option>
                      <option value="whichever_higher">Whichever Higher</option>
                    </select>
                  </div>
                  {variableRule.calculation_type !== "slab" && (
                    <div className="form-group">
                      <label className="form-label">Flat Percentage (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        className="input-default w-full"
                        value={variableRule.flat_percentage}
                        onChange={(e) => setVariableRule({ ...variableRule, flat_percentage: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            <button onClick={saveVariableRule} className="btn-primary flex items-center gap-2" disabled={saving}>
              <FloppyDisk size={18} />
              {saving ? "Saving..." : "Save Variable Fee Rule"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "slabs" && (
        <div className="card-flat p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Varela Round' }}>
              Slab Configuration
            </h3>
            <button onClick={addSlab} className="btn-secondary flex items-center gap-2">
              <Plus size={18} />
              Add Slab
            </button>
          </div>

          {slabRules.length === 0 ? (
            <p className="text-[#A1A1AA] py-8 text-center">No slabs configured. Click "Add Slab" to create one.</p>
          ) : (
            <table className="table-dense">
              <thead>
                <tr>
                  <th>Min Value</th>
                  <th>Max Value</th>
                  <th>Percentage (%)</th>
                  <th>Apply On</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {slabRules.map((slab, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="number"
                        className="input-default w-full"
                        value={slab.min_value}
                        onChange={(e) => updateSlab(index, "min_value", parseFloat(e.target.value) || 0)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="input-default w-full"
                        value={slab.max_value}
                        onChange={(e) => updateSlab(index, "max_value", parseFloat(e.target.value) || 0)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.1"
                        className="input-default w-full"
                        value={slab.percentage}
                        onChange={(e) => updateSlab(index, "percentage", parseFloat(e.target.value) || 0)}
                      />
                    </td>
                    <td>
                      <select
                        className="input-default w-full"
                        value={slab.apply_on_slab ? "slab" : "full"}
                        onChange={(e) => updateSlab(index, "apply_on_slab", e.target.value === "slab")}
                      >
                        <option value="slab">Slab Only</option>
                        <option value="full">Full Amount</option>
                      </select>
                    </td>
                    <td>
                      <button onClick={() => removeSlab(index)} className="btn-ghost text-[#FF2A2A]">
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <button onClick={saveSlabRules} className="btn-primary flex items-center gap-2 mt-6" disabled={saving}>
            <FloppyDisk size={18} />
            {saving ? "Saving..." : "Save Slab Rules"}
          </button>
        </div>
      )}

      {activeTab === "incentive" && (
        <div className="card-flat p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Varela Round' }}>
            Incentive Configuration
          </h3>
          
          {/* Incentive Type Selection */}
          <div className="form-group mb-6">
            <label className="form-label">Incentive Type</label>
            <div className="flex gap-3">
              {[["revenue", "Revenue-Based"], ["roi", "ROI-Based"]].map(([val, label]) => (
                <label key={val} className={`flex items-center gap-2 p-3 border cursor-pointer transition-colors ${incentiveRule.incentive_type === val ? "border-[#FFEB00] bg-[#1A1A00]" : "border-[#2A2A2A] hover:border-[#FFEB00]/50"}`}>
                  <input type="radio" name="incentive_type" value={val} checked={incentiveRule.incentive_type === val}
                    onChange={e => setIncentiveRule({ ...incentiveRule, incentive_type: e.target.value })} className="accent-[#FFEB00]" />
                  <span className="text-sm font-medium">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {incentiveRule.incentive_type === "revenue" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Trigger Start (Revenue)</label>
                <input type="number" className="input-default w-full" value={incentiveRule.trigger_start}
                  onChange={(e) => setIncentiveRule({ ...incentiveRule, trigger_start: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label className="form-label">Increment Step</label>
                <input type="number" className="input-default w-full" value={incentiveRule.increment_step}
                  onChange={(e) => setIncentiveRule({ ...incentiveRule, increment_step: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label className="form-label">Extra % per Step</label>
                <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.extra_percentage_per_step}
                  onChange={(e) => setIncentiveRule({ ...incentiveRule, extra_percentage_per_step: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label className="form-label">Cap Percentage (%)</label>
                <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.cap_percentage}
                  onChange={(e) => setIncentiveRule({ ...incentiveRule, cap_percentage: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          )}

          {incentiveRule.incentive_type === "roi" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Starting ROI Threshold (e.g. 4)</label>
                  <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.roi_threshold}
                    onChange={(e) => setIncentiveRule({ ...incentiveRule, roi_threshold: parseFloat(e.target.value) || 0 })} />
                  <p className="text-xs text-[#71717A] mt-1">Incentive triggers when ROI reaches this value</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Base Incentive % at Trigger</label>
                  <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.roi_base_percentage}
                    onChange={(e) => setIncentiveRule({ ...incentiveRule, roi_base_percentage: parseFloat(e.target.value) || 0 })} />
                  <p className="text-xs text-[#71717A] mt-1">% applied when ROI first hits threshold</p>
                </div>
                <div className="form-group">
                  <label className="form-label">ROI Increment Step (e.g. 1x)</label>
                  <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.roi_increment_step}
                    onChange={(e) => setIncentiveRule({ ...incentiveRule, roi_increment_step: parseFloat(e.target.value) || 0 })} />
                  <p className="text-xs text-[#71717A] mt-1">Every +Nx ROI adds more incentive</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Incremental % per Step</label>
                  <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.roi_increment_percentage}
                    onChange={(e) => setIncentiveRule({ ...incentiveRule, roi_increment_percentage: parseFloat(e.target.value) || 0 })} />
                  <p className="text-xs text-[#71717A] mt-1">Additional % added per ROI step</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Maximum ROI Cap (e.g. 10x)</label>
                  <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.roi_max_cap}
                    onChange={(e) => setIncentiveRule({ ...incentiveRule, roi_max_cap: parseFloat(e.target.value) || 0 })} />
                  <p className="text-xs text-[#71717A] mt-1">0 = no cap</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Maximum Incentive Cap (%)</label>
                  <input type="number" step="0.1" className="input-default w-full" value={incentiveRule.roi_max_percentage_cap}
                    onChange={(e) => setIncentiveRule({ ...incentiveRule, roi_max_percentage_cap: parseFloat(e.target.value) || 0 })} />
                  <p className="text-xs text-[#71717A] mt-1">0 = no cap</p>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Calculation Base</label>
                <select className="input-default w-full" value={incentiveRule.roi_calculation_base}
                  onChange={(e) => setIncentiveRule({ ...incentiveRule, roi_calculation_base: e.target.value })}>
                  <option value="total_fee">Total Fee (Fixed + Variable + Commission)</option>
                  <option value="revenue">Revenue</option>
                </select>
                <p className="text-xs text-[#71717A] mt-1">What the incentive percentage applies to</p>
              </div>
              {/* Example preview */}
              {incentiveRule.roi_threshold > 0 && incentiveRule.roi_base_percentage > 0 && (
                <div className="p-3 bg-[#0A1A0A] border border-[#00E676]/20 text-xs text-[#A1A1AA]">
                  <span className="text-[#00E676] font-medium">Example:</span> ROI {incentiveRule.roi_threshold}x → +{incentiveRule.roi_base_percentage}%
                  {incentiveRule.roi_increment_step > 0 && incentiveRule.roi_increment_percentage > 0 && (
                    <>, every +{incentiveRule.roi_increment_step}x → +{incentiveRule.roi_increment_percentage}%</>
                  )}
                  {incentiveRule.roi_max_cap > 0 && <>, capped at ROI {incentiveRule.roi_max_cap}x</>}
                  {incentiveRule.roi_max_percentage_cap > 0 && <>, max {incentiveRule.roi_max_percentage_cap}%</>}
                </div>
              )}
            </div>
          )}

          <button onClick={saveIncentiveRule} className="btn-primary flex items-center gap-2 mt-6" disabled={saving}>
            <FloppyDisk size={18} />
            {saving ? "Saving..." : "Save Incentive Rule"}
          </button>
        </div>
      )}

      {activeTab === "commission" && (
        <div className="card-flat p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Varela Round' }}>
            Commission Configuration
          </h3>
          
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="commission-enabled"
                checked={commissionRule.enabled}
                onChange={(e) => setCommissionRule({ ...commissionRule, enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="commission-enabled" className="form-label mb-0">
                Enable Commission
              </label>
            </div>

            {commissionRule.enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group col-span-2">
                  <label className="form-label">Team Member Name</label>
                  <input
                    type="text"
                    className="input-default w-full"
                    value={commissionRule.team_member_name}
                    onChange={(e) => setCommissionRule({ ...commissionRule, team_member_name: e.target.value })}
                    placeholder="Enter team member name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Fixed Payout ({client?.currency})</label>
                  <input
                    type="number"
                    className="input-default w-full"
                    value={commissionRule.fixed_payout}
                    onChange={(e) => setCommissionRule({ ...commissionRule, fixed_payout: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Variable Percentage (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="input-default w-full"
                    value={commissionRule.variable_percentage}
                    onChange={(e) => setCommissionRule({ ...commissionRule, variable_percentage: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Based On</label>
                  <select
                    className="input-default w-full"
                    value={commissionRule.based_on}
                    onChange={(e) => setCommissionRule({ ...commissionRule, based_on: e.target.value })}
                  >
                    <option value="total">Total Billable (Fixed + Variable)</option>
                    <option value="revenue">Revenue (Gross Revenue)</option>
                    <option value="fixed">Fixed Fee Only</option>
                    <option value="variable">Variable Fee Only</option>
                  </select>
                </div>
              </div>
            )}

            <button onClick={saveCommissionRule} className="btn-primary flex items-center gap-2" disabled={saving}>
              <FloppyDisk size={18} />
              {saving ? "Saving..." : "Save Commission Rule"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RulesConfiguration;
